import {useEffect, useMemo, useState} from 'react';
import JobCard from './JobCard';
import JobDetails from './JobDetails';
import {Button} from '@/components/ui/button';


export default function JobResults({ searchParamsData = {}, searchResultData = [], setIsResumePopupOpen }) {
    const [searchParams, setSearchParams] = useState(searchParamsData);
    const [searchResult, setSearchResult] = useState(searchResultData);

    const [jobs, setJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [selectedJobIndex, setSelectedJobIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalJobs, setTotalJobs] = useState(searchResultData.length || 0);

    const JOBS_PER_PAGE = 7;

    const pagedJobs = useMemo(() => {
        const startIndex = (currentPage - 1) * JOBS_PER_PAGE;
        const endIndex = startIndex + JOBS_PER_PAGE;
        return jobs.slice(startIndex, endIndex);
    }, [jobs, currentPage, JOBS_PER_PAGE]);

    useEffect(() => {
        setSearchParams(searchParamsData);
    }, [searchParamsData]);

    useEffect(() => {
        setSearchResult(searchResultData);
    }, [searchResultData]);


    // Update jobs when search results change from props
    useEffect(() => {
        if (searchResult && searchResult.length > 0) {
            setJobs(searchResult);
            setTotalJobs(searchResult.length);
            setSelectedJob(searchResult[0]);
            setSelectedJobIndex(0);
            setTotalPages(Math.ceil(searchResult.length / JOBS_PER_PAGE));
            setCurrentPage(1);
        }
    }, [searchResult, JOBS_PER_PAGE]);

    useEffect(() => {
        setIsLoading(false);
    }, [currentPage]);

    useEffect(() => {
        if (pagedJobs.length > 0) {
            setSelectedJob(pagedJobs[0]);
            setSelectedJobIndex(0);
        } else {
            setSelectedJob(null);
        }
    }, [pagedJobs]);

    // Function to handle page change
    const handlePageChange = (page) => {
        if (page === currentPage || isLoading) return;
        setIsLoading(true);
        setCurrentPage(page);
    };

    const handlePreviousJob = () => {
        if (!selectedJob || pagedJobs.length === 0) return;

        const currentIndex = pagedJobs.findIndex(job => job.id === selectedJob.id);
        let newIndex;

        if (currentIndex > 0) {
            newIndex = currentIndex - 1;
        } else {
            newIndex = pagedJobs.length - 1;
        }

        if (newIndex !== currentIndex) {
            setSelectedJob(pagedJobs[newIndex]);
            setSelectedJobIndex(newIndex);
        }
    };

    const handleNextJob = () => {
        if (!selectedJob || pagedJobs.length === 0) return;

        const currentIndex = pagedJobs.findIndex(job => job.id === selectedJob.id);
        let newIndex;

        if (currentIndex < pagedJobs.length - 1) {
            newIndex = currentIndex + 1;
        } else {
            newIndex = 0;
        }

        if (newIndex !== currentIndex) {
            setSelectedJob(pagedJobs[newIndex]);
            setSelectedJobIndex(newIndex);
        }
    };

    // Generate pagination buttons
    const renderPagination = () => {
        const buttons = [];
        const maxDisplayedPages = 5; // Show at most 5 page buttons

        let startPage = Math.max(1, currentPage - Math.floor(maxDisplayedPages / 2));
        let endPage = Math.min(totalPages, startPage + maxDisplayedPages - 1);

        // Adjust if showing less than maxDisplayedPages
        if (endPage - startPage + 1 < maxDisplayedPages) {
            startPage = Math.max(1, endPage - maxDisplayedPages + 1);
        }

        // Previous button
        buttons.push(
            <Button
                key="prev"
                variant="outline"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-gray-600 rounded-md"
                aria-label="Previous page"
            >
                {"‹"}
            </Button>
        );

        // Page buttons
        for (let i = startPage; i <= endPage; i++) {
            const isCurrentPage = currentPage === i;

            buttons.push(
                <Button
                    key={i}
                    variant={isCurrentPage ? "ghost" : "outline"}
                    onClick={() => handlePageChange(i)}
                    disabled={isLoading}
                    className={`px-3 py-1 rounded-md ${isCurrentPage ? 'bg-blue-100/50 text-blue-700' : 'text-gray-700'}`}
                    aria-label={`Page ${i}`}
                    aria-current={isCurrentPage ? 'page' : undefined}
                >
                    {i}
                </Button>
            );
        }

        // Next button
        buttons.push(
            <Button
                key="next"
                variant="outline"
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || isLoading}
                className="px-3 py-1 text-gray-600 rounded-md"
                aria-label="Next page"
            >
                {"›"}
            </Button>
        );

        return buttons;
    };

    // Render search criteria badges based on searchParams
    const renderSearchCriteria = () => {
        if (!searchParams) return null;

        return (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
                {searchParams.job_title && (
                    <div className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-lg border border-gray-100 flex items-center gap-1 hover:border-gray-200 transition-colors duration-200">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-gray-500">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
                        </svg>
                        {searchParams.job_title}
                    </div>
                )}

                {searchParams.location && (
                    <div className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-lg border border-gray-100 flex items-center gap-1 hover:border-gray-200 transition-colors duration-200">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-gray-500">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                        </svg>
                        {searchParams.location}
                    </div>
                )}

                {searchParams.employment_type && (
                    <div className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-lg border border-gray-100 flex items-center gap-1 hover:border-gray-200 transition-colors duration-200">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-gray-500">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {searchParams.employment_type === 'full_time' ? 'Full-time' :
                         searchParams.employment_type === 'part_time' ? 'Part-time' :
                         searchParams.employment_type}
                    </div>
                )}

                {searchParams.remote_mode && (
                    <div className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-lg border border-gray-100 flex items-center gap-1 hover:border-gray-200 transition-colors duration-200">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-gray-500">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                        </svg>
                        {searchParams.remote_mode === 'at_work' ? 'On-site' :
                         searchParams.remote_mode === 'remote' ? 'Remote' :
                         searchParams.remote_mode === 'hybrid' ? 'Hybrid' :
                         searchParams.remote_mode}
                    </div>
                )}

                {searchParams.seniority_levels && (
                    <div className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-lg border border-gray-100 flex items-center gap-1 hover:border-gray-200 transition-colors duration-200">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-gray-500">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                        </svg>
                        {searchParams.seniority_levels === 'mid_senior_level' ? 'Mid Senior Level' :
                         searchParams.seniority_levels === 'entry_level' ? 'Entry Level' :
                         searchParams.seniority_levels === 'director' ? 'Director' :
                         searchParams.seniority_levels === 'associate' ? 'Associate' :
                         searchParams.seniority_levels === 'internship' ? 'Internship' :
                         searchParams.seniority_levels}
                    </div>
                )}

                {searchParams.time_recency && (
                    <div className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-lg border border-gray-100 flex items-center gap-1 hover:border-gray-200 transition-colors duration-200">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-gray-500">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                        </svg>
                        {searchParams.time_recency === 'day' ? 'Last day' :
                         searchParams.time_recency === 'week' ? 'Last week' :
                         searchParams.time_recency === 'month' ? 'Last month' :
                         searchParams.time_recency}
                    </div>
                )}

                {searchParams.resume_file_url && (
                    <button
                        onClick={() => setIsResumePopupOpen(true)}
                        className="relative group px-2.5 py-1 bg-gray-50 text-gray-700 rounded-lg border border-gray-100 flex items-center gap-1 hover:border-gray-200 transition-colors duration-200"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                             stroke="currentColor" className="w-3.5 h-3.5 text-gray-500">
                            <path strokeLinecap="round" strokeLinejoin="round"
                                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
                        </svg>
                        View Resume

                        {/* Hover tooltip */}
                        <div className="absolute hidden group-hover:block top-full left-1/2 transform -translate-x-1/2 mt-2 bg-black text-white text-xs rounded-md py-2 px-3 z-10 whitespace-nowrap leading-relaxed text-center">
                            Click to view PDF
                        </div>
                    </button>
                )}

                {searchParams.job_preferences && (
                    <div className="relative group">
                        <div className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-lg border border-gray-100 flex items-center gap-1 hover:border-gray-200 transition-colors duration-200">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-gray-500">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                            </svg>
                            View Job Preferences
                        </div>

                        {/* Hover Tooltip */}
                        <div className="absolute hidden group-hover:block top-full left-1/2 transform -translate-x-1/2 mt-2 bg-black text-white text-xs rounded-md py-2 px-4 z-10 max-w-[550px] w-max break-words leading-relaxed text-left whitespace-pre-line">
                            {searchParams.job_preferences}
                        </div>
                    </div>
                )}

                {searchParams && (
                    <div className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-lg border border-gray-100 flex items-center gap-1 hover:border-gray-200 transition-colors duration-200">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-gray-500">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3.75v16.5M2.25 12h19.5M6.375 17.25a4.875 4.875 0 004.875-4.875V12m6.375 5.25a4.875 4.875 0 01-4.875-4.875V12m-9 8.25h16.5a1.5 1.5 0 001.5-1.5V5.25a1.5 1.5 0 00-1.5-1.5H3.75a1.5 1.5 0 00-1.5 1.5v13.5a1.5 1.5 0 001.5 1.5zm12.621-9.44c-1.409 1.41-4.242 1.061-4.242 1.061s-.349-2.833 1.06-4.242a2.25 2.25 0 013.182 0 2.25 2.25 0 010 3.182z" />
                        </svg>
                        {searchParams.selected_package.charAt(0).toUpperCase() + searchParams.selected_package.slice(1)}
                    </div>
                )}

                {searchParams && searchParams.created_at && (
                    <div className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-lg border border-gray-100 flex items-center gap-1 hover:border-gray-200 transition-colors duration-200">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-gray-500">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                        </svg>
                        {searchParams.created_at}
                    </div>
                )}
            </div>
        );
    };

    // Conditional content rendering based on auth state and data
    const renderContent = () => {
        // Default: Show the job results
        return (
            <>
                {/* Job Results Box */}
                <div className="w-full bg-white rounded-2xl shadow-[0_2px_15px_rgba(0,0,0,0.1),0_5px_30px_rgba(0,0,0,0.15)] overflow-hidden border border-gray-100">
                    {/* Top Information Banner */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-5">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <span className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                        <path d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625z" />
                                        <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
                                    </svg>
                                </span>
                                <span className="text-gray-700">Found <span className="font-semibold">{totalJobs} jobs</span> from a quick scan — upgrade for deeper AI matching and access to a 5× larger job pool!</span>
                            </div>
                            <button
                                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 active:scale-95 transition-all text-white font-semibold py-2.5 px-5 rounded-lg shadow-sm hover:shadow"
                                onClick={() => {
                                    const pricingSection = document.getElementById('pricing');
                                    if (pricingSection) {
                                        pricingSection.scrollIntoView({ behavior: 'smooth' });
                                    }
                                }}
                            >
                                <span className="text-yellow-300">🔒</span> Unlock Full Results
                            </button>
                        </div>
                    </div>

                    {/* Search Criteria Bar */}
                    <div className="bg-white border-y border-gray-100 px-6 py-3">
                        {renderSearchCriteria()}
                    </div>

                    {/* Main Content */}
                    <div className="flex flex-col lg:flex-row">
                        {/* Left side - Job List */}
                        <div className="w-full lg:w-[360px] lg:border-r border-gray-100 h-[630px] overflow-hidden">
                            {isLoading ? (
                                <div className="h-full flex justify-center items-center">
                                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <div className="h-full">
                                    {/* Always render exactly 7 job card slots */}
                                    {Array.from({ length: JOBS_PER_PAGE }).map((_, index) => {
                                        const job = pagedJobs[index];

                                        return job ? (
                                            <JobCard
                                                key={job.id}
                                                job={job}
                                                isSelected={selectedJob?.id === job.id}
                                                onClick={() => {
                                                    setSelectedJob(job);
                                                    setSelectedJobIndex(index);
                                                }}
                                            />
                                        ) : (
                                            <div
                                                key={`empty-${index}`}
                                                className="w-full border-b border-gray-100 py-[25px] px-4 h-[89px]"
                                            ></div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Right side - Job Details */}
                        <div className="hidden lg:block lg:flex-1 h-[630px] overflow-auto bg-gray-50/30">
                            {selectedJob ? (
                                <JobDetails
                                    job={selectedJob}
                                    translations={{
                                        aboutCompany: 'About Company',
                                        responsibilities: 'Responsibilities',
                                        requirements: 'Requirements',
                                        applyNow: 'Apply Now'
                                    }}
                                />
                            ) : (
                                <div className="h-full flex justify-center items-center">
                                    <p className="text-gray-500">{isLoading ? "Loading..." : "Select a job to view details"}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom Bar */}
                    <div className="bg-gray-50 border-t border-gray-100">
                        <div className="flex flex-col sm:flex-row">
                            {/* Pagination */}
                            <div className="w-full sm:w-[360px] p-4 border-b sm:border-b-0 sm:border-r border-gray-100">
                                <div className="flex justify-center">
                                    <div className="flex gap-2">
                                        {renderPagination()}
                                    </div>
                                </div>
                            </div>

                            {/* Job Navigation */}
                            <div className="flex-1 flex">
                                <button
                                    className="flex-1 py-4 text-center transition-colors bg-white text-gray-700 font-medium border-r border-gray-100 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                                    onClick={handlePreviousJob}
                                    disabled={selectedJobIndex === 0 || pagedJobs.length === 0}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                                        </svg>
                                        Previous Job
                                    </div>
                                </button>
                                <button
                                    className="flex-1 py-4 text-center transition-colors bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                                    onClick={handleNextJob}
                                    disabled={selectedJobIndex === pagedJobs.length - 1 || pagedJobs.length === 0}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        Next Job
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                        </svg>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    return renderContent();
};